import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { NavLink, Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiSlice, useLogoutMutation } from '../../api/apiSlice';
import logo from '../../img/logo.png';
import logoMenu from '../../img/Logo-menu.png';
import ThemeToggle from '../common/ThemeToggle';
import LanguageSwitcher from '../common/LanguageSwitcher';
import NotificationsBell from '../common/NotificationsBell';

import {
  LayoutDashboard,
  BookOpen,
  BookMarked,
  Compass,
  BarChart3,
  User,
  LogOut,
  FilePlus,
  Menu,
  UserCircle2,
  Settings,
  LogIn,
  Home,
  Sparkles,
  MessageSquare,
  Award,
  ListChecks,
  Wallet,
  Phone,
  RotateCcw,
  Activity,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

// Меню для каждой роли. Лейблы — i18n-ключи, разрешаются через t() в момент рендера,
// чтобы переключение языка обновляло сайдбар без перезагрузки.
const buildRoleMenus = () => ({
  student: [
    { key: 'nav.home', path: '/dashboard/student', icon: <Home size={20} /> },
    { key: 'nav.courses', path: '/courses', icon: <Compass size={20} /> },
    { key: 'nav.myCourses', path: '/my-courses', icon: <BookMarked size={20} /> },
    { key: 'nav.certificates', path: '/my-certificates', icon: <Award size={20} /> },
    { key: 'nav.quizzes', path: '/dashboard/student/quizzes', icon: <Sparkles size={20} /> },
    { key: 'nav.profile', path: '/profile', icon: <User size={20} /> },
    { key: 'nav.settings', path: '/settings', icon: <Settings size={20} /> },
  ],
  instructor: [
    { key: 'nav.dashboard', path: '/dashboard/instructor', icon: <LayoutDashboard size={20} /> },
    { key: 'nav.myCourses', path: '/dashboard/instructor/my-courses', icon: <BookOpen size={20} /> },
    { key: 'nav.createCourse', path: '/dashboard/instructor/create-course', icon: <FilePlus size={20} /> },
    { key: 'nav.finance', path: '/dashboard/instructor/finance', icon: <Wallet size={20} /> },
    { key: 'nav.analytics', path: '/dashboard/instructor/analytics', icon: <BarChart3 size={20} /> },
    { key: 'nav.profile', path: '/profile', icon: <User size={20} /> },
    { key: 'nav.settings', path: '/settings', icon: <Settings size={20} /> },
  ],
  admin: [
    { key: 'nav.adminPanel', path: '/dashboard/admin', icon: <LayoutDashboard size={20} /> },
    { key: 'nav.users', path: '/dashboard/admin/users', icon: <User size={20} /> },
    { key: 'nav.courses', path: '/dashboard/admin/course-management', icon: <BookOpen size={20} /> },
    { key: 'nav.payments', path: '/dashboard/admin/payments', icon: <Phone size={20} /> },
    { key: 'nav.studentLookup', path: '/dashboard/admin/student-lookup', icon: <RotateCcw size={20} /> },
    { key: 'nav.reviews', path: '/dashboard/admin/reviews', icon: <MessageSquare size={20} /> },
    { key: 'nav.orders', path: '/dashboard/admin/orders', icon: <ListChecks size={20} /> },
    { key: 'nav.categories', path: '/dashboard/admin/categories', icon: <Settings size={20} /> },
    { key: 'nav.auditLog', path: '/dashboard/admin/audit-log', icon: <Activity size={20} /> },
    { key: 'nav.profile', path: '/profile', icon: <User size={20} /> },
    { key: 'nav.settings', path: '/settings', icon: <Settings size={20} /> },
  ],
});

const getPrimaryRole = (userRoles) => {
  if (!Array.isArray(userRoles) || userRoles.length === 0) return 'student';
  if (userRoles.includes('admin')) return 'admin';
  if (userRoles.includes('instructor')) return 'instructor';
  return 'student';
};

const getAvailableMenuItems = (userRoles) => {
  const menus = buildRoleMenus();
  if (!Array.isArray(userRoles)) return menus.student;
  const items = [];
  const seen = new Set();
  userRoles.forEach((role) => {
    (menus[role] || []).forEach((item) => {
      if (!seen.has(item.path)) {
        items.push(item);
        seen.add(item.path);
      }
    });
  });
  return items.length ? items : menus.student;
};

const SafeAvatar = ({ src, alt = 'Аватар', className = 'w-full h-full object-cover' }) => {
  const [hasError, setHasError] = useState(false);
  if (!src || hasError) {
    return <UserCircle2 className="text-slate-500 dark:text-slate-300 w-10 h-10" />;
  }
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setHasError(true)}
      onLoad={() => setHasError(false)}
    />
  );
};

// Упрощённый layout для неавторизованных посетителей (например, страница деталей курса).
const PublicShell = () => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 overflow-x-hidden">
      <header className="sticky top-0 z-20 h-16 sm:h-20 bg-white/80 dark:bg-slate-950/80 backdrop-blur-lg border-b border-slate-200 dark:border-slate-800 flex items-center px-4 sm:px-6 lg:px-8 justify-between">
        <Link to="/" className="flex items-center gap-3 min-w-0">
          <img src={logo} alt="Uaqyt" className="h-9 sm:h-12 shrink-0" />
        </Link>

        <div className="flex items-center gap-2 sm:gap-3">
          <LanguageSwitcher compact />
          <ThemeToggle />
          <Link
            to="/auth"
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 text-white dark:bg-white dark:text-slate-900 px-3 sm:px-4 py-2 text-sm font-medium hover:opacity-90 transition"
          >
            <LogIn size={16} />
            <span className="hidden xs:inline sm:inline">{t('common.login')}</span>
          </Link>
        </div>
      </header>

      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
};

const DashboardLayout = () => {
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [isSidebarOpen, setSidebarOpen] = useState(false);

  // ─── Desktop collapsible sidebar (Notion/Linear style) ─────────────
  //   collapsed=true → видны только иконки (w-16). Зашёл на иконку
  //   мышкой (hover) → сайдбар расширяется ПОВЕРХ контента до w-64,
  //   контент не дёргается. Кликнул chevron → расширяется фиксировано,
  //   контент сдвигается. Состояние храним в localStorage.
  //
  //   На мобиле эта механика не работает — там slide-in drawer.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('sidebar_collapsed') === '1';
    } catch {
      return false;
    }
  });
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const desktopExpanded = !sidebarCollapsed || sidebarHovered;

  const toggleSidebarCollapse = () => {
    const next = !sidebarCollapsed;
    setSidebarCollapsed(next);
    try {
      localStorage.setItem('sidebar_collapsed', next ? '1' : '0');
    } catch (_) { /* ignore */ }
  };

  const userRoleData = useMemo(() => {
    const userRoles = user?.roles || [];
    const roleDisplayNames = (userRoles.length ? userRoles : ['student']).map((r) =>
      t(`roles.${r}`, { defaultValue: r })
    );
    return {
      primaryRole: getPrimaryRole(userRoles),
      availableMenuItems: getAvailableMenuItems(userRoles),
      roleDisplayNames,
      hasMultipleRoles: userRoles.length > 1,
    };
  }, [user?.roles, t]);

  useEffect(() => {
    if (isSidebarOpen) setSidebarOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const [logoutMutation] = useLogoutMutation();
  const handleLogout = async () => {
    // Серверный logout критичен: он удаляет запись Session из БД, освобождая
    // слот в лимите одновременных сессий. Без этого юзер «выходит» только
    // локально, а на сервере сессия живёт до expiresAt (7 дней).
    try {
      await logoutMutation().unwrap();
    } catch {
      // Если запрос упал — всё равно очистим локально (mutation сама вызовет logOut).
    }
    dispatch(apiSlice.util.resetApiState());
    navigate('/');
  };

  // Гость — отдаём упрощённый shell, чтобы публичный просмотр курса не открывался "на весь экран без обвязки".
  if (!user) {
    return <PublicShell />;
  }

  // expanded управляет: видны ли тексты, центрируются ли иконки.
  // onToggle опционально — кнопка-сворачиватель есть только в desktop-сайдбаре.
  const SidebarContent = ({ expanded = true, onToggle = null }) => (
    <>
      {/* Logo: при expanded — полный wide-логотип. При collapsed — обрезаем
          ТОТ ЖЕ логотип контейнером w-10 overflow-hidden, показывая только
          левую часть (брендовая иконка-галочка). Никаких самодельных
          квадратов — реальный бренд. */}
      <div className="h-20 shrink-0 flex items-center justify-between border-b border-slate-200 dark:border-slate-800 px-3">
        {/* Лого в сайдбаре — статичная картинка, не кликабельна. Раньше был
            <Link to="/"> и клик уводил юзера из дашборда на лендинг, что
            ломало контекст работы. */}
        <div className="flex items-center min-w-0 overflow-hidden">
          {expanded ? (
            <img
              src={logo}
              alt="Uaqyt Logo"
              className="shrink-0 h-20 w-auto"
            />
          ) : (
            // Logo-menu.png — отдельная квадратная иконка от тебя для свёрнутого
            // состояния. Чисто, без обрезок.
            <img
              src={logoMenu}
              alt="Uaqyt"
              className="shrink-0 h-10 w-10 object-contain"
            />
          )}
        </div>
        {onToggle && expanded && (
          <button
            type="button"
            onClick={onToggle}
            className="shrink-0 p-1.5 rounded-lg text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-slate-800 transition"
            title="Свернуть меню"
          >
            <ChevronLeft size={18} />
          </button>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-4 min-h-0">
        {userRoleData.hasMultipleRoles && expanded && (
          <div className="mx-3 mb-4 p-3 bg-blue-50 dark:bg-slate-800 rounded-lg">
            <div className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
              {t('nav.yourRoles')}:
            </div>
            <div className="text-sm text-blue-600 dark:text-blue-400">
              {userRoleData.roleDisplayNames.join(', ')}
            </div>
          </div>
        )}

        <ul className="space-y-1 px-3">
          {userRoleData.availableMenuItems.map((item) => (
            <li key={item.path}>
              <NavLink
                to={item.path}
                end={item.path === `/dashboard/${userRoleData.primaryRole}`}
                title={!expanded ? t(item.key) : undefined}
                className={({ isActive }) =>
                  `group/item flex items-center gap-3 rounded-lg transition-colors text-slate-700 dark:text-slate-300 hover:bg-blue-50 dark:hover:bg-slate-800 hover:text-blue-600 ${
                    expanded ? 'p-3' : 'p-2 justify-center'
                  } ${
                    isActive ? 'bg-blue-100 dark:bg-slate-800 text-blue-700 dark:text-white font-semibold shadow-sm' : ''
                  }`
                }
              >
                <span className="shrink-0">{item.icon}</span>
                <span
                  className={`whitespace-nowrap transition-opacity ${
                    expanded ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'
                  }`}
                >
                  {t(item.key)}
                </span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <div className="shrink-0 p-3 border-t border-slate-200 dark:border-slate-800">
        <button
          onClick={handleLogout}
          title={!expanded ? t('common.logout') : undefined}
          className={`flex items-center gap-3 w-full rounded-lg transition-colors text-slate-700 dark:text-slate-300 hover:bg-red-50 dark:hover:bg-slate-800 hover:text-red-600 ${
            expanded ? 'p-3' : 'p-2 justify-center'
          }`}
        >
          <LogOut size={20} className="shrink-0" />
          <span
            className={`whitespace-nowrap transition-opacity ${
              expanded ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'
            }`}
          >
            {t('common.logout')}
          </span>
        </button>
      </div>
    </>
  );

  return (
    // overflow-x-hidden на корне предотвращает горизонтальный скролл из-за
    // случайного overflow внутренних элементов (long URLs, фиксированные media).
    // На мобилке без этого юзер видит только левую часть контента и думает
    // что страница "обрезана".
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 overflow-x-hidden">
      {/* Mobile drawer: flex+flex-col критично — без display:flex flex-1 в nav
          не работает, и длинное меню (8+ пунктов админа) обрезается. */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-white dark:bg-slate-950 border-r border-slate-200 dark:border-slate-800 flex flex-col transform transition-transform duration-300 ease-in-out lg:hidden ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <SidebarContent expanded={true} />
      </aside>

      {/* Desktop sidebar — collapsible. Ширина анимируется между w-16 и w-64.
          При collapsed+hover: расширяется ПОВЕРХ контента через `shadow` —
          контент НЕ дёргается, лучший UX (Notion/Linear-стиль). */}
      <aside
        onMouseEnter={() => sidebarCollapsed && setSidebarHovered(true)}
        onMouseLeave={() => setSidebarHovered(false)}
        className={`hidden lg:flex fixed inset-y-0 left-0 z-30 flex-col bg-white dark:bg-slate-950 border-r border-slate-200 dark:border-slate-800 transition-[width] duration-200 ease-out ${
          desktopExpanded ? 'w-64' : 'w-16'
        } ${
          sidebarCollapsed && sidebarHovered ? 'shadow-2xl' : ''
        }`}
      >
        <SidebarContent expanded={desktopExpanded} onToggle={toggleSidebarCollapse} />
        {/* Кнопка "развернуть" — видна только когда collapsed (не hover). */}
        {sidebarCollapsed && !sidebarHovered && (
          <button
            type="button"
            onClick={toggleSidebarCollapse}
            className="absolute top-4 -right-3 w-6 h-6 rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-sm flex items-center justify-center text-slate-500 hover:text-blue-600 transition"
            title="Развернуть меню"
          >
            <ChevronRight size={14} />
          </button>
        )}
      </aside>

      {/* min-w-0 на флекс-чайлде — критично. Без него длинный inline-content
          (например, длинный URL или большое видео) растягивает колонку шире
          viewport и ломает мобильный layout.
          Margin не зависит от hover — только от collapsed-state. Это значит
          контент не дёргается когда юзер наводит курсор на сайдбар. */}
      <div className={`flex flex-col flex-1 min-w-0 transition-[margin] duration-200 ease-out ${
        sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
      }`}>
        <header className="sticky top-0 z-20 h-16 sm:h-20 bg-white/80 dark:bg-slate-950/80 backdrop-blur-lg border-b border-slate-200 dark:border-slate-800 flex items-center px-4 sm:px-6 lg:px-8 justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-slate-600 dark:text-slate-400 hover:text-blue-600"
          >
            <Menu size={24} />
          </button>

          <div className="flex-1"></div>

          <div className="flex items-center gap-2 sm:gap-4">
            <LanguageSwitcher compact />
            <NotificationsBell />
            <ThemeToggle />

            <div className="hidden sm:flex flex-col items-end">
              <span className="font-medium text-sm">{user?.name}</span>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {userRoleData.roleDisplayNames[0]}
              </span>
            </div>

            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full overflow-hidden border-2 border-slate-200 dark:border-slate-700 shrink-0">
              <SafeAvatar src={user?.avatarUrl} alt={`${user?.name} аватар`} />
            </div>
          </div>
        </header>

        {/* p-3 на мобилке вместо p-6 — экономит 24px ширины (важно на 360px экранах). */}
        <main className="flex-1 p-3 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>

      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default DashboardLayout;
